import React, { useState, useEffect } from 'react';
import { Customer, Subscription, Invoice, PaymentMethod, PlanDetail } from '../types';
import { api } from '../services/api';
import { useSettings } from './SettingsContext';
import PortalSubscription from './portal/PortalSubscription';
import PortalInvoices from './portal/PortalInvoices';
import PortalUsage from './portal/PortalUsage';
import PortalNotifications from './portal/PortalNotifications';
import { PortalWorkspaces } from './portal/PortalWorkspaces';
import FeedbackModal from './FeedbackModal';
import { CURRENCIES } from '../constants';

const MOCK_CARDS: PaymentMethod[] = [
  { id: 'card_1', brand: 'visa', last4: '4242', expiry: '12/26', isDefault: true, nickname: 'Primary Card' },
  { id: 'card_2', brand: 'mastercard', last4: '8812', expiry: '08/25', isDefault: false, nickname: 'Backup Method' },
];

const DEFAULT_CREDITS = {
  used: 0,
  totalAllocated: 0,
  remaining: 0,
};

const normalizeId = (value: unknown) => String(value || '').trim().toLowerCase();

const subscriptionBelongsToCustomer = (subscription: Subscription, customerId: string) => {
  const raw = subscription as Subscription & { customer_id?: string; customer?: { id?: string }; email?: string };
  const targetId = normalizeId(customerId);
  return [raw.customerId, raw.customer_id, raw.customer?.id]
    .filter(Boolean)
    .some(value => normalizeId(value) === targetId);
};

const isActiveSubscription = (subscription: Subscription) => {
  const status = normalizeId(subscription.status);
  return status === 'active' || status === 'trial';
};

const getPlanLabel = (plan?: PlanDetail, subscription?: Subscription | null) => {
  if (plan) return (plan as PlanDetail & { name?: string }).name || plan.tier;
  return subscription?.plan ? String(subscription.plan) : 'No Active Plan';
};

const findPlanForSubscription = (plans: PlanDetail[], subscription: Subscription | null) => {
  if (!subscription) return undefined;
  const subscriptionPlan = normalizeId(subscription.plan);
  return plans.find(plan => [plan.id, plan.tier, (plan as PlanDetail & { name?: string }).name]
    .filter(Boolean)
    .some(value => normalizeId(value) === subscriptionPlan));
};

const formatPlanCurrency = (value: unknown, plan?: PlanDetail) => {
  const amount = Number(value || 0);
  const currencyCode = plan?.currency || 'INR';
  const currency = CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0];
  return currency.symbol + (Number.isFinite(amount) ? amount.toLocaleString(currency.code === 'INR' ? 'en-IN' : 'en-US') : '0');
};

const CustomerPortal: React.FC<{ customerId?: string }> = ({ customerId }) => {
  const { settings } = useSettings();
  const [activeTab, setActiveTab] = useState<'overview' | 'subscription' | 'workspaces' | 'usage' | 'billing' | 'alerts'>('overview');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [plans, setPlans] = useState<PlanDetail[]>([]);
  const [loading, setLoading] = useState(true);

  const branding = settings.portalBranding;

  useEffect(() => {
    const loadPortalData = async () => {
      try {
        let custId = customerId || localStorage.getItem('portal_customer_id');

        const customersList = await api.customers.getAll();
        if (!custId) {
          setLoading(false);
          return;
        }

        const targetCustId = custId;
        if (!targetCustId) {
          setLoading(false);
          return;
        }

        const [c, subs, invs, planData] = await Promise.all([
          api.customers.getOne(targetCustId).catch(() => customersList.find(cust => cust.id === targetCustId) || null),
          api.subscriptions.getAll(),
          api.invoices.getAll(),
          api.plans.getAll()
        ]);
        
        const rawCredits = (c?.credits || {}) as any;
        const safeCustomer = c ? {
          ...c,
          credits: {
            used: Number(rawCredits.used || 0),
            totalAllocated: Number(rawCredits.totalAllocated || 0),
            remaining: Math.max(0, Number(rawCredits.totalAllocated || 0) - Number(rawCredits.used || 0)),
          }
        } : null;
        
        setCustomer(safeCustomer);
        setSubscription(subs.find(s => subscriptionBelongsToCustomer(s, targetCustId) && isActiveSubscription(s)) || null);
        setInvoices(invs.filter(i => i.customerId === targetCustId));
        setPlans(planData.filter(plan => plan.isActive !== false));
      } catch (err) {
        console.error("Portal error", err);
      } finally {
        setLoading(false);
      }
    };
    loadPortalData();
  }, [customerId]);

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-slate-50">
       <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-400 font-black uppercase text-[10px] tracking-widest">Signing you in...</p>
       </div>
    </div>
  );

  if (!customer) return (
    <div className="h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <h2 className="text-xl font-black text-slate-900">Customer profile not found</h2>
        <p className="text-sm text-slate-500 mt-2">Please contact support.</p>
      </div>
    </div>
  );

  const credits = {
    ...DEFAULT_CREDITS,
    ...(customer.credits || {}),
  };

  const creditUsagePercent = credits.totalAllocated > 0
    ? Math.min(100, (credits.used / credits.totalAllocated) * 100)
    : 0;
  const currentPlan = findPlanForSubscription(plans, subscription);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans transition-colors duration-300">
      {/* Portal Header */}
      <header className="bg-white border-b border-slate-200 px-8 py-6 sticky top-0 z-30 shadow-sm">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
           <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-inner overflow-hidden border border-slate-100 p-2">
                 <img src={branding.logoUrl} className="max-h-full max-w-full object-contain" alt="Brand Logo" />
              </div>
              <span className="text-xl font-black text-slate-900 tracking-tight">{branding.name}</span>
           </div>
           <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                 <p className="text-sm font-black text-slate-900">{customer.name}</p>
                 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">ID: {customer.id}</p>
              </div>
              <button onClick={() => setShowFeedbackModal(true)} className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-100 transition-all">Feedback</button>
              <img src={`https://picsum.photos/40/40?u=${customer.id}`} className="w-10 h-10 rounded-xl border-2 border-slate-100 shadow-sm" alt="Avatar" />
           </div>
        </div>
      </header>

      {/* Portal Tabs */}
      <div className="bg-white border-b border-slate-200 sticky top-[89px] z-20 overflow-x-auto no-scrollbar">
        <div className="max-w-6xl mx-auto flex">
           {(['overview', 'subscription', 'workspaces', 'usage', 'billing', 'alerts'] as const).map(tab => (
             <button 
               key={tab}
               onClick={() => setActiveTab(tab)}
               className={`px-8 py-4 text-[10px] font-black uppercase tracking-[0.2em] border-b-4 transition-all shrink-0 ${activeTab === tab ? 'border-indigo-600 text-indigo-600 bg-indigo-50/20' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
             >
               {tab === 'workspaces' ? 'Workspaces & Add-ons' : tab === 'billing' ? 'Account & Billing' : tab === 'alerts' ? 'Notifications' : tab}
             </button>
           ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-6xl mx-auto w-full p-8 space-y-12">
        {showFeedbackModal && <FeedbackModal onClose={() => setShowFeedbackModal(false)} />}
        {activeTab === 'overview' && (
          <div className="space-y-12 animate-in fade-in duration-500">
             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-slate-900 rounded-[3rem] p-12 text-white shadow-2xl relative overflow-hidden group">
                   <div className="relative z-10">
                      <h1 className="text-4xl font-black tracking-tight mb-2">Welcome Back.</h1>
                      <p className="text-slate-400 text-lg">Your workspace <b className="text-white">{customer.name}</b> is currently {subscription ? 'healthy and active' : 'ready for activation'}.</p>
                      <div className="mt-12 flex flex-wrap gap-12">
                         <div>
                            <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Next Bill Amount</p>
                            <p className="text-4xl font-black">{subscription ? formatPlanCurrency(subscription.mrr ?? currentPlan?.price ?? 0, currentPlan) : 'No Active Plan'}</p>
                         </div>
                         <div>
                            <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Renewal Date</p>
                            <p className="text-4xl font-black">{subscription?.nextBillingDate ? new Date(subscription.nextBillingDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric' }) : 'Not scheduled'}</p>
                         </div>
                      </div>
                   </div>
                   <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none group-hover:scale-110 transition-transform duration-700">
                      <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2v20"/><path d="m17 5-5-3-5 3"/><path d="m17 19-5 3-5-3"/><path d="M2 12h20"/><path d="m5 7-3 5 3 5"/><path d="m19 7 3 5-3 5"/></svg>
                   </div>
                </div>
                
                 <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm flex flex-col justify-between">
                   <div className="flex justify-between items-start mb-6">
                     <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Resource Credits</h3>
                     <span className="text-[9px] font-black text-indigo-600 uppercase bg-indigo-50 px-2 py-0.5 rounded-md">Live Meter</span>
                   </div>
                   <div className="space-y-6">
                      <div className="flex justify-between items-end">
                         <span className="text-4xl font-black text-slate-900">{credits.used.toLocaleString()}</span>
                         <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">of {credits.totalAllocated.toLocaleString()} allocated</span>
                      </div>
                      <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                         <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${creditUsagePercent}%` }}></div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setActiveTab('usage')} className="flex-1 py-3.5 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all cursor-pointer">Usage & Top-Ups</button>
                        <button onClick={() => setActiveTab('subscription')} className="flex-1 py-3.5 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all cursor-pointer">Upgrade Plan</button>
                      </div>
                   </div>
                </div>
             </div>

             {/* Growth Upgrade Spotlight Strip */}
             <div className="p-6 bg-gradient-to-r from-indigo-900 via-slate-900 to-indigo-950 rounded-3xl text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xl">
               <div className="flex items-center gap-4">
                 <div className="w-12 h-12 bg-indigo-600/30 border border-indigo-400/30 rounded-2xl flex items-center justify-center text-indigo-300">
                   <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m13 2-2 2.5h3L11 8l5-1-6 8 1.5-3.5H9L13 2z"/><path d="M4 14a8 8 0 0 0 16 0"/></svg>
                 </div>
                 <div>
                   <h4 className="text-sm font-black uppercase tracking-wider text-white">
                     Unlock Higher Rate Limits & Priority Workspaces
                   </h4>
                   <p className="text-xs text-slate-300 mt-0.5">
                     Upgrade your tier today to get 5x more included compute tokens, zero queue delays, and dedicated SLAs.
                   </p>
                 </div>
               </div>
               <button
                 onClick={() => setActiveTab('subscription')}
                 className="px-6 py-3 bg-white text-slate-900 hover:bg-indigo-50 rounded-xl text-xs font-black uppercase tracking-wider transition-all shrink-0 cursor-pointer shadow-md"
               >
                 Explore Upgrades →
               </button>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-[2.5rem] p-10 border border-slate-200 shadow-sm">
                   <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-6">Payment Method</h3>
                   <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                         <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
                         </div>
                         <div>
                           <p className="font-black text-slate-700">•••• 4242 (Visa Primary)</p>
                           <p className="text-[10px] text-emerald-600 font-bold uppercase">Auto-Renewal Active</p>
                         </div>
                      </div>
                      <button onClick={() => setActiveTab('billing')} className="text-[10px] font-black text-indigo-600 uppercase hover:underline">Update</button>
                   </div>
                </div>
                <div className="bg-indigo-50 border border-indigo-100 rounded-[2.5rem] p-10 flex flex-col justify-center">
                   <div className="flex items-center gap-2 mb-2">
                     <h3 className="text-sm font-black text-indigo-900 uppercase tracking-widest">Priority Success & Growth</h3>
                     <span className="px-2 py-0.5 bg-indigo-200/60 text-indigo-800 rounded text-[9px] font-black uppercase">24/7 SLA</span>
                   </div>
                   <p className="text-xs text-indigo-700/70 font-medium leading-relaxed">Need help tailoring custom volume quotas, enterprise add-ons, or custom invoices for your accounting department?</p>
                   <button onClick={() => alert('Our billing specialist is connected and ready to assist.')} className="mt-6 text-[10px] font-black text-indigo-600 uppercase w-fit hover:underline cursor-pointer">Chat with billing concierge</button>
                </div>
             </div>
          </div>
        )}

        {activeTab === 'subscription' && (
          <PortalSubscription 
            subscription={subscription}
            plans={plans}
            onUpdate={(u) => setSubscription(prev => prev ? { ...prev, ...u } : null)}
            onCancel={(r, f) => alert(`Requesting cancellation...`)} 
          />
        )}

        {activeTab === 'workspaces' && (
          <PortalWorkspaces
            customerId={customer.id}
            subscriptionId={subscription?.id}
            plans={plans}
          />
        )}

        {activeTab === 'usage' && (
          <div className="space-y-12">
            <PortalUsage customer={customer} />
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="space-y-12 animate-in slide-in-from-right-8 duration-500">
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white rounded-[2.5rem] p-10 border border-slate-200 shadow-sm space-y-8">
                   <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Billing Address</h3>
                   <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Business Address</label>
                        <textarea 
                           className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-xs font-bold text-slate-700 outline-none focus:ring-4 focus:ring-indigo-50 resize-none min-h-[100px]"
                           defaultValue={customer.billingAddress || "123 Market St, Floor 4\nSan Francisco, CA 94105\nUnited States"}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">VAT / Tax ID</label>
                          <input type="text" defaultValue={customer.gstin || "US991234567"} className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-xs font-bold text-slate-700 outline-none focus:ring-1 focus:ring-indigo-500" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">PO Number</label>
                          <input type="text" defaultValue={customer.poNumber || "PO-8821"} className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-xs font-bold text-slate-700 outline-none focus:ring-1 focus:ring-indigo-500" />
                        </div>
                      </div>
                   </div>
                </div>

                <div className="bg-white rounded-[2.5rem] p-10 border border-slate-200 shadow-sm">
                   <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-8">Payment Methods</h3>
                   <div className="space-y-4">
                      {MOCK_CARDS.map(card => (
                        <div key={card.id} className={`p-6 rounded-3xl border-2 flex justify-between items-center ${card.isDefault ? 'border-indigo-600 bg-indigo-50/50 shadow-md' : 'border-slate-100 bg-white'}`}>
                           <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-xs font-black text-slate-400 shadow-sm uppercase">{card.brand.charAt(0)}</div>
                              <div>
                                 <p className="text-sm font-black text-slate-900">{card.nickname || `•••• ${card.last4}`}</p>
                                 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">Exp {card.expiry}</p>
                              </div>
                           </div>
                           {card.isDefault && <span className="px-2 py-1 bg-indigo-600 text-white text-[8px] font-black uppercase rounded">Default</span>}
                        </div>
                      ))}
                      <button className="w-full py-4 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl mt-4">+ Add New Method</button>
                   </div>
                </div>
             </div>

             <PortalInvoices invoices={invoices} />
          </div>
        )}

        {activeTab === 'alerts' && (
          <PortalNotifications />
        )}
      </main>

      <footer className="p-12 text-center text-slate-400 text-[10px] font-bold uppercase tracking-widest border-t border-slate-100 bg-white">
         {branding.footerText}
      </footer>
    </div>
  );
};

export default CustomerPortal;
